import dayjs from 'dayjs'
import { BaseTaskFragment, Communication, QuestionnaireResponseItem } from 'generated/graphql'
import { getCssSelectorForLinkId } from 'utils/helpers'
import { AssessmentType } from 'components/Clinical/Assessments/constants'
import { AssessmentStatus } from 'fhir/QuestionnaireResponse/constants'
import { TaskStatus } from 'fhir/Task/constants'
import { MAX_QA_ROUNDS } from './constants'

export type TaskOwnerOrRequester = BaseTaskFragment['owner'] | BaseTaskFragment['requester']

export interface QaRoles {
  qaNurse?: TaskOwnerOrRequester
  caseManager?: TaskOwnerOrRequester
}

export const scrollToElement = <T extends HTMLElement>(
  currentContainerElement: T,
  elementId: string,
  offset: number
): void => {
  if (currentContainerElement && elementId) {
    const elementIdValue = `#${elementId}`
    const targetElement = document.body.querySelector(elementIdValue) as HTMLElement
    if (targetElement) {
      const targetScrollTop = targetElement.offsetTop - offset
      currentContainerElement.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth'
      })
    }
  }
}

export const determineQARoles = (
  task?: BaseTaskFragment,
  assessmentStatus?: AssessmentStatus
): QaRoles => {
  switch (assessmentStatus) {
    case AssessmentStatus.PENDING_QA:
      return { qaNurse: task?.owner, caseManager: task?.requester }
    case AssessmentStatus.PENDING_QA_EDITS:
      return { qaNurse: task?.requester, caseManager: task?.owner }
    case AssessmentStatus.COMPLETED || AssessmentStatus.PENDING_APPROVAL:
      return { qaNurse: task?.owner, caseManager: task?.requester }
    default:
      return {}
  }
}

// Text is for the comment title
interface ItemWithPosition {
  position: number
  linkId: string
  text: string
}

export type SortedItemsMap = Record<string, ItemWithPosition>

export const populateAssessmentQuestionsMap = (
  assessmentType: AssessmentType,
  questions: QuestionnaireResponseItem[] | null,
  parentItem?: QuestionnaireResponseItem
): Record<string, ItemWithPosition> | null => {
  let position = 1
  const questionMap: Record<string, ItemWithPosition> = {}

  const addQuestionsToMapForCareAssessment = (response: QuestionnaireResponseItem[]): void => {
    const flattenItem = (item: QuestionnaireResponseItem[]): void => {
      item.forEach((entry) => {
        const { linkId: entryLinkId, text: entryText } = entry
        if (entryLinkId && entryText) {
          questionMap[entry.linkId] = {
            position,
            linkId: entryLinkId,
            text: entryText
          }
          position++

          if (entry.item) {
            flattenItem(entry.item)
          }
        }
      })
    }
    response.forEach((item) => {
      flattenItem([item])
    })
  }

  const addQuestionsToMapForPat = (
    items: QuestionnaireResponseItem[],
    parent?: QuestionnaireResponseItem
  ): void => {
    for (const item of items) {
      if (item && item.linkId && item.text) {
        const splitLinkId = item.linkId.split('/')
        const isNestedQuestion = splitLinkId[splitLinkId.length - 1] === 'question'
        const isNotes = splitLinkId[splitLinkId.length - 1] === 'notes'

        // Notes are not included in the question map, grouped with parent
        if (!isNotes) {
          let itemWithPosition: ItemWithPosition
          if (isNestedQuestion && parent && parent.text) {
            itemWithPosition = {
              linkId: item.linkId,
              text: parent.text,
              position
            }
          } else {
            itemWithPosition = {
              linkId: item.linkId,
              text: item.text,
              position
            }
          }
          questionMap[itemWithPosition.linkId] = itemWithPosition
          position++
          if (item.item && item.item.length > 0) {
            addQuestionsToMapForPat(item.item, item)
          }
        }
      }
    }
  }
  if (questions) {
    if (assessmentType === AssessmentType.PAT) {
      addQuestionsToMapForPat(questions, parentItem)
    } else if (
      [
        AssessmentType.SOC,
        AssessmentType.SERVICE_PLAN,
        AssessmentType.RECERT,
        AssessmentType.FUA,
        AssessmentType.DISCHARGE,
        AssessmentType.SUPERVISORY,
        AssessmentType.ROC
      ].includes(assessmentType as AssessmentType)
    ) {
      addQuestionsToMapForCareAssessment(questions)
    }
    return questionMap
  }
  return null
}

export const sortCommentsByItemsMap = (
  comments: Communication[],
  itemsMap: SortedItemsMap
): Communication[] => {
  const sortedComments = comments.sort((a, b) => {
    const keyA = a?.topic?.id || ''
    const keyB = b?.topic?.id || ''
    const positionA = itemsMap[keyA] ? itemsMap[keyA].position : -1
    const positionB = itemsMap[keyB] ? itemsMap[keyB].position : -1
    return positionA - positionB
  })

  return sortedComments
}

export const getIsFinalRoundQA = (qaTaskList: BaseTaskFragment[]): boolean => {
  // This is the list of tasks that have not been cancelled (e.g. reassigned).
  const qaTasksSortedDesc = qaTaskList
    .sort((a, b) => {
      if (!a.executionPeriod?.start && !b.executionPeriod?.start) return 0
      if (!a.executionPeriod?.start) return 1
      if (!b.executionPeriod?.start) return -1

      return dayjs(a.executionPeriod.end).valueOf() - dayjs(b.executionPeriod.end).valueOf()
    })
    .reverse()
  const nonCancelledTasks = qaTasksSortedDesc.filter((t) => t.status !== TaskStatus.CANCELLED)

  // Because we don't have an initial QA task to create the assessment, we add 1
  // to the non cancelled tasks to simulate the total number of "passes" made on
  // the assessment. We have to divide by 2 because every full cycle of QA is
  // 2 passes (the initial edit, and then the QA).
  return Math.ceil((nonCancelledTasks.length + 1) / 2) > MAX_QA_ROUNDS
}

export const getAllCommentsResolved = (comments: Communication[]): boolean => {
  return comments.every((comment) => comment.status === 'completed')
}

export const getAllCommentsHaveNotes = (comments: Communication[]): boolean => {
  return comments.every((comment) => comment?.note?.every((n) => n?.text))
}

export const getCommentQuestionLinkId = (comment: Communication): string | null => {
  return comment?.topic?.id ?? null
}

export const generateCommentBoxId = (comment: Communication): string => {
  const questionLinkId = getCommentQuestionLinkId(comment)

  return comment && questionLinkId
    ? `${getCssSelectorForLinkId(questionLinkId)}_comment_${comment.id}`
    : ''
}
